import { VEvent } from 'src/schedules/schedule.entity';
import { AddLocationDocument, CalendarLocationLinkFragment, FetchCurrentDefaultLocationDocument, LocationWithParentsFragment, UpdateCalendarLocationDocument, UpdateLocationDocument } from 'types/db';
import { type ArgoCalendarLocationInsertInput } from '@redviking/argonaut-util/types/db-schema/argo/calendar_location';
import { type ArgoLocationAttributeInsertInput } from '@redviking/argonaut-util/types/db-schema/argo/location_attribute';
import { validateLocation } from './locations.validations';
import { gqlClient } from 'src/util/gql-client';
import type { EntityModifyParameters, ExtendedEntityParameters } from 'src/components/EntityDetail';
import { getListChanges } from 'src/util/composables/watch-list-changes';
import type { ValidationResult } from 'types';

export type LocationEntity = Pick<LocationWithParentsFragment,
    'id' |
    'name' |
    'is_default' |
    'description' |
    'parentLocationId' |
    'location_class_id'
> & {
    calendarLinks: {
        active_range: string,
        calendar: {
          id: string,
          name: string,
        }
        id: string,
        meta: unknown | Record<string, unknown>,
    }[],
    location_attributes: string[],
};

type VCalendarLocationEvent = VEvent & {
    calendar: {
        id: string,
        name: string,
    },
    infinite?: boolean,
};

export function getLocationClassIcon (locClassName: string) {
    switch (locClassName) {
        case 'Plant':
            return 'mdi-factory';
        case 'Area':
            return 'mdi-go-kart-track';
        case 'Line':
            return 'mdi-map-marker-path';
        case 'Station':
            return 'mdi-tablet-dashboard';
        default:
            return 'mdi-map-marker';
    }
}

export function parseArgoCalendarLocationEvents (dbCalendarEvents: CalendarLocationLinkFragment[]): VCalendarLocationEvent[] {
    return dbCalendarEvents?.map<VCalendarLocationEvent>((event) => {
        const times = event.active_range.replace(')', ']');
        const startDate = new Date(`${JSON.parse(times)[0]}:00.000Z`);
        const endDate = new Date(`${JSON.parse(times)[1]}:00.000Z`);

        return {
            id: event.id,
            calendar: {
                id: event.calendar.id,
                name: event.calendar.name,
            },
            name: event.calendar.name,
            start: startDate,
            end: endDate,
            color: 'gray',
            priority: 0,
            timed: true,
        };
    });
}

const unsetCurrentDefault = async (currentEntityId: string, isDefault: boolean): Promise<boolean> => {
    const {
        argo_location,
    } = await gqlClient.request({
        variables: {},
        document: FetchCurrentDefaultLocationDocument,
    });
    if (isDefault) {
        if (argo_location && argo_location.length) {
            const defaultLocationId = argo_location[0].id;
            if (defaultLocationId !== currentEntityId) {
                return true;
            }
        }
    } else if (!argo_location || !argo_location.length || argo_location[0].id === currentEntityId) {
        throw new Error('There must always be a default location.');
    }
    return false;
};

export const createLocation = async (payload: EntityModifyParameters<'location'>): Promise<ValidationResult> => {
    const { entity } = payload;
    const validationResult = validateLocation(entity);
    if (validationResult.status !== 'success') {
        return validationResult;
    }
    
    try {
        const isDefault = entity.is_default || false;
        await gqlClient.request({
            document: AddLocationDocument,
            variables: {
                newDefault: await unsetCurrentDefault(entity.id, isDefault),
                location: {
                    id: entity.id,
                    name: entity.name,
                    is_default: isDefault,
                    description: entity.description,
                    parent_location_id: entity.parentLocationId,
                    location_class_id: entity.location_class_id,
                    location_attributes: {
                        data: entity.location_attributes.map(a => ({
                            attribute_id: a,
                        })),
                    },
                    calendar_locations: {
                        data: entity.calendarLinks.map(l => ({
                            active_range: l.active_range,
                            id: l.id,
                            calendar_id: l.calendar.id,
                            meta: l.meta,
                        })),
                    },
                },
            },
        });
        return {
            status: 'success',
        };
    } catch (err) {
        return {
            status: 'error',
            errorMessage: `Error saving Location: "${JSON.stringify(err)}"`,
        };
    }
};

export const saveLocation = async (payload: ExtendedEntityParameters<'location'>): Promise<ValidationResult> => {
    const { entity, oldEntity } = payload;
    const validationResult = validateLocation(entity);
    if (validationResult.status !== 'success') {
        return validationResult;
    }

    try {
        const insert: ArgoLocationAttributeInsertInput[] = entity.location_attributes.filter(attr => !oldEntity?.location_attributes.find(a => a === attr)).map(a => ({
            location_id: entity.id,
            attribute_id: a,
        }));
        const deleteIds = oldEntity?.location_attributes.filter(attr => !entity.location_attributes.find(a => a === attr)) || [];
        const calLinkChanges = getListChanges(entity.calendarLinks, oldEntity?.calendarLinks || [], { partialChanges: false, deep: true, format: 'plc' });

        const isDefault = entity.is_default || false;
        await gqlClient.request({
            document: UpdateLocationDocument,
            variables: {
                locationId: entity.id,
                attributesToLink: insert,
                attributesToUnlink: deleteIds,
                newDefault: await unsetCurrentDefault(entity.id, isDefault),
                calendarIdsThatAreBeingUpdated: calLinkChanges.updates.map(i => i.id),
                updatedCalendarLocations: calLinkChanges.updates.map<ArgoCalendarLocationInsertInput>(i => ({
                    id: i.id,
                    meta: i.meta,
                    calendar_id: i.calendar.id,
                    location_id: entity.id,
                    active_range: i.active_range,
                })),
                calendarsToLink: calLinkChanges.inserts.map<ArgoCalendarLocationInsertInput>(i => ({
                    id: i.id,
                    meta: i.meta,
                    calendar_id: i.calendar.id,
                    location_id: entity.id,
                    active_range: i.active_range,
                })),
                location: {
                    id: entity.id,
                    name: entity.name,
                    is_default: isDefault,
                    description: entity.description,
                    parent_location_id: entity.parentLocationId,
                    location_class_id: entity.location_class_id,
                },
            },
        });
        for (const calLinkUpdate of calLinkChanges.updates) {
            await gqlClient.request({
                document: UpdateCalendarLocationDocument,
                variables: {
                    calendarLocationId: calLinkUpdate.id,
                    calendarLocation: {
                        meta: calLinkUpdate.meta,
                        calendar_id: calLinkUpdate.calendar.id,
                        active_range: calLinkUpdate.active_range,
                    },
                },
            });
        }
        return {
            status: 'success',
        };
    } catch (err) {
        return {
            status: 'error',
            errorMessage: `Error saving Location: "${JSON.stringify(err)}"`,
        };
    }
};
